import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../common/Card';
import { Badge } from '../common/Badge';

export function OddsComparison({ data, market, accessStatus }) {
  const { t } = useTranslation();
  const [showAIEval, setShowAIEval] = useState(false);
  const [lastUpdate] = useState(new Date().toLocaleTimeString());
  const isAuthRequired = accessStatus?.isAuthRequired;
  const isMembershipRequired = accessStatus?.isMembershipRequired;

  if (!data?.comparison?.length) {
    const emptyState = isAuthRequired
      ? { text: t('auth.loginRequired', '请先登录'), className: 'text-accent' }
      : isMembershipRequired
      ? { text: t('membership.noAccess', '没有会员权限'), className: 'text-accent-warm' }
      : { text: t('detail.noOddsData'), className: 'text-gray-500' };
    return (
      <Card>
        <h3 className="font-semibold text-accent mb-4 flex items-center gap-2">
          <span>⚖️</span> {t('detail.oddsComparison')}
        </h3>
        <div className={`${emptyState.className} text-sm text-center py-8`}>
          {emptyState.text}
        </div>
      </Card>
    );
  }

  const { comparison, bestOdds } = data;

  // Find best odds for highlighting
  const bestTeamAOdds = bestOdds?.teamA?.odds || Math.max(...comparison.map(p => p.teamA?.implied || 0));
  const bestTeamBOdds = bestOdds?.teamB?.odds || Math.max(...comparison.map(p => p.teamB?.implied || 0));

  return (
    <Card>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-accent flex items-center gap-2">
          <span>⚖️</span> {t('detail.oddsComparison')}
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAIEval(!showAIEval)}
            className="px-3 py-1 text-xs rounded bg-accent/20 text-accent hover:bg-accent/30 transition-colors flex items-center gap-1"
          >
            🤖 {t('detail.aiEval')}
          </button>
          <button className="text-gray-400 hover:text-white transition-colors">⟳</button>
        </div>
      </div>

      {/* AI Evaluation Panel */}
      {showAIEval && (
        <div className="mb-4 p-4 bg-accent/10 border border-accent/30 rounded-lg">
          <div className="text-sm text-accent-soft mb-2">{t('detail.aiEvaluation')}</div>
          <p className="text-sm text-gray-300">
            {t('detail.aiEvalText', { 
              teamA: market?.teamA, 
              teamB: market?.teamB,
              bestPlatformA: bestOdds?.teamA?.source || 'Polymarket',
              bestPlatformB: bestOdds?.teamB?.source || 'Polymarket'
            })}
          </p>
        </div>
      )}

      {/* Comparison Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-400 border-b border-glass-border">
              <th className="text-left py-2 px-2">{t('detail.platform')}</th>
              <th className="text-center py-2 px-2">{market?.teamA || 'Team A'}</th>
              <th className="text-center py-2 px-2">{market?.teamB || 'Team B'}</th>
              <th className="text-right py-2 px-2">{t('detail.margin')}</th>
            </tr>
          </thead>
          <tbody>
            {comparison.map((platform, i) => {
              const isTeamABest = platform.teamA?.implied === bestTeamAOdds || 
                (bestOdds?.teamA?.source?.toLowerCase() === platform.source);
              const isTeamBBest = platform.teamB?.implied === bestTeamBOdds ||
                (bestOdds?.teamB?.source?.toLowerCase() === platform.source);

              return (
                <tr key={i} className="border-b border-glass-border/50 hover:bg-white/5">
                  <td className="py-3 px-2">
                    <span className="font-medium">{platform.sourceName}</span>
                  </td>
                  <td className="text-center py-3 px-2">
                    <div className="flex items-center justify-center gap-2">
                      <span className={`font-mono ${isTeamABest ? 'text-positive font-bold' : ''}`}>
                        {platform.teamA?.display || `${platform.teamA?.implied?.toFixed(1)}%`}
                      </span>
                      {isTeamABest && (
                        <Badge variant="positive" className="text-[10px] px-1">⭐ {t('detail.best')}</Badge>
                      )}
                    </div>
                  </td>
                  <td className="text-center py-3 px-2">
                    <div className="flex items-center justify-center gap-2">
                      <span className={`font-mono ${isTeamBBest ? 'text-positive font-bold' : ''}`}>
                        {platform.teamB?.display || `${platform.teamB?.implied?.toFixed(1)}%`}
                      </span>
                      {isTeamBBest && (
                        <Badge variant="positive" className="text-[10px] px-1">⭐ {t('detail.best')}</Badge>
                      )}
                    </div>
                  </td>
                  <td className="text-right py-3 px-2 text-gray-400">
                    {platform.margin ? `${platform.margin.toFixed(1)}%` : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-glass-border flex items-center justify-between text-xs text-gray-400">
        <span>{t('detail.bestOddsNote')}</span>
        <span>{t('detail.updatedAt')} {lastUpdate}</span>
      </div>
    </Card>
  );
}
